import { Icon, Input, TableColumn, TableRow } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { EditState, IReportingUser, IValidationResult } from '../../../models';
import { TextHelper } from '../../../utilities';
import { CursorPointer } from '../../common/styles/globalStyles';

export const ActionColumn = styled(TableColumn).attrs<{}>({
	className: 'tws-table-column',
})`
	&.tws-table-column {
		min-width: 36px !important;
		width: 5%;
	}
`;
ActionColumn.displayName = 'ActionColumn';

export const EmailColumn = styled(TableColumn).attrs<{}>({
	className: 'tws-table-column',
})`
	&.tws-table-column {
		min-width: 220px !important;
		width: 35%;
	}
`;
EmailColumn.displayName = 'EmailColumn';

export const EmailIcon = styled(Icon)`
	padding-right: 10px;
`;
EmailIcon.displayName = 'EmailIcon';

export const EmailIconDisabled = styled(Icon)`
	padding-right: 10px;
	color: #cccccc;
`;
EmailIconDisabled.displayName = 'EmailIconDisabled';

export const NameColumn = styled(TableColumn).attrs<{}>({
	className: 'tws-table-column',
})`
	&.tws-table-column {
		min-width: 140px !important;
		width: 30%;
	}
`;
NameColumn.displayName = 'NameColumn';

export const EmailIconHandler = (props: { isClosed?: boolean; hasSentEmail: boolean; editState: EditState }) => {
	const { isClosed, hasSentEmail, editState } = props;
	return !isClosed || hasSentEmail || editState === EditState.Add ? <EmailIconDisabled name='email' /> : <EmailIcon name='email' />;
};

export const EmailHandler = (props: {
	isClosed?: boolean;
	isSendingEmail?: boolean;
	hasSentEmail: boolean;
	editState: EditState;
	NotifyUser?: () => void;
}) => {
	const { isClosed, isSendingEmail, hasSentEmail, editState, NotifyUser } = props;
	if (isClosed && !isSendingEmail && !hasSentEmail && editState !== EditState.Add) {
		return (
			<CursorPointer onClick={NotifyUser!}>
				<EmailIconHandler isClosed={isClosed} hasSentEmail={hasSentEmail} editState={editState} />
			</CursorPointer>
		);
	} else {
		return <EmailIconHandler isClosed={isClosed} hasSentEmail={hasSentEmail} editState={editState} />;
	}
};

export interface IReportingUserRowProps {
	user: IReportingUser;
	reportingUsers: IReportingUser[];
	index: number;
	surveyId: number;
	isSurveyAdmin?: boolean;
	isClosed?: boolean;
	isSendingEmail?: boolean;
	validationResults?: IValidationResult;
	AddUser: () => void;
	DeleteUser: (user: IReportingUser) => void;
	UpdateUser: (user: IReportingUser) => void;
	NotifyUser?: (surveyId: number, user: IReportingUser) => void;
}

export class ReportingUserRow extends React.PureComponent<IReportingUserRowProps> {
	constructor(props: IReportingUserRowProps) {
		super(props);

		this.updateFirstName = this.updateFirstName.bind(this);
		this.updateLastName = this.updateLastName.bind(this);
		this.updateEmail = this.updateEmail.bind(this);
	}

	public render() {
		const { index, isSurveyAdmin, surveyId, isClosed, isSendingEmail, reportingUsers, user } = this.props;
		const readOnlyEmail = isSurveyAdmin && user.editState !== EditState.Add;
		const propertyErrors = (this.props.validationResults ? this.props.validationResults.propertyErrors : []) || {};
		if (user.isCreator) {
			return (
				<TableRow key={index} record={user.reportingUserId}>
					<NameColumn>{user.firstName}</NameColumn>
					<NameColumn>{user.lastName}</NameColumn>
					<EmailColumn>{user.emailAddress}</EmailColumn>
					<ActionColumn />
				</TableRow>
			);
		} else {
			const isUniqueEmail = TextHelper.isUniqueEmail(
				user.emailAddress,
				reportingUsers.map((u) => u.emailAddress),
			);
			const errors = (propertyErrors && propertyErrors[user.id]) || [];
			return (
				<TableRow key={index} record={user.reportingUserId}>
					<NameColumn>
						<Input
							onChange={(e: any) => this.updateFirstName(e, user)}
							value={user.firstName}
							size='sm'
							placeholder={ContentConstants.PLACEHOLDER_REPORTING_FIRSTNAME}
							error={errors.indexOf(ContentConstants.REPORTINGUSER_VALIDATE_FIRSTNAME) > -1}
							message={ContentConstants.PLACEHOLDER_REPORTING_ERROR_ENTER_VALUE}
						/>
					</NameColumn>
					<NameColumn>
						<Input
							onChange={(e: any) => this.updateLastName(e, user)}
							value={user.lastName}
							size='sm'
							placeholder={ContentConstants.PLACEHOLDER_REPORTING_LASTNAME}
							error={errors.indexOf(ContentConstants.REPORTINGUSER_VALIDATE_LASTNAME) > -1}
							message={ContentConstants.PLACEHOLDER_REPORTING_ERROR_ENTER_VALUE}
						/>
					</NameColumn>
					<EmailColumn>
						<Input
							onChange={(e: any) => this.updateEmail(e, user)}
							value={user.emailAddress}
							readonly={readOnlyEmail}
							size='sm'
							placeholder={ContentConstants.PLACEHOLDER_REPORTING_EMAIL}
							error={errors.indexOf(ContentConstants.REPORTINGUSER_VALIDATE_EMAILADDRESS) > -1}
							message={
								!isUniqueEmail
									? ContentConstants.PLACEHOLDER_REPORTING_ERROR_ENTER_UNIQUE_EMAIL
									: ContentConstants.PLACEHOLDER_REPORTING_ERROR_ENTER_EMAIL
							}
						/>
					</EmailColumn>
					<ActionColumn>
						{isSurveyAdmin && (
							<EmailHandler
								isClosed={isClosed}
								isSendingEmail={isSendingEmail}
								hasSentEmail={user.hasSentEmail}
								editState={user.editState}
								NotifyUser={() => this.props.NotifyUser!(surveyId, user)}
							/>
						)}
						<CursorPointer onClick={() => this.props.DeleteUser(user)}>
							<Icon name='delete' />
						</CursorPointer>
					</ActionColumn>
				</TableRow>
			);
		}
	}

	private updateFirstName(e: any, user: IReportingUser) {
		this.props.UpdateUser({ ...user, firstName: e.target.value });
	}
	private updateLastName(e: any, user: IReportingUser) {
		this.props.UpdateUser({ ...user, lastName: e.target.value });
	}
	private updateEmail(e: any, user: IReportingUser) {
		this.props.UpdateUser({ ...user, emailAddress: e.target.value });
	}
}
